import { withWebNfcChecks } from 'airkey-mfa-react';
import { ErrorScreen_DEPRECIATED } from '@/components/ErrorScreen/ErrorScreen_DEPRECIATED';
import { NFCNotEnabled } from '@/pages/DeviceError/NFCNotEnabled';
import { WEB_NFC_PERMISSIONS_VIDEO } from '../3ds/TapCardPage/model/constants';
import { CopyUrlButton } from '../global/CopyUrlButton/CopyUrlButton';

const CopyHelper = <CopyUrlButton message="" />;

interface ErrorMapType {
  [key: string]: {
    title: string;
    body: string;
    action: string;
    helper: null | JSX.Element;
  };
}

const errorMap: ErrorMapType = {
  ERROR_NFC_NOT_SUPPORTED: {
    title: 'This Browser Does Not Support NFC',
    body: 'You may need to open this link in Chrome if that is not your current browser.',
    action: 'AndroidNFCNotSupported',
    helper: CopyHelper,
  },
  ERROR_DEVICE_NFC_NOT_ENABLED: {
    title: 'NFC is not enabled on your device',
    body: 'Please enable NFC Permissions in your device settings. You may need to reload this page for the changes to take effect',
    action: 'AndroidNFCDeviceDisabled',
    helper: null,
  },
  ERROR_NEED_NFC_PERMISSIONS: {
    title: 'NFC is not enabled on your Browser',
    body: 'Please enable NFC Permissions in your browser settings. You may need to reload this page for the changes to take effect',
    action: 'AndroidNFCBrowserDisabled',
    helper: null,
  },
  ERROR_NFC_PERMISSIONS_NOT_ALLOWED: {
    title: 'Unable to access NFC Permissions',
    body: 'You may need to open this link in Chrome if that is not your current browser.',
    action: 'AndroidNFCPermissionsNotAllowed',
    helper: CopyHelper,
  },
  GENERIC: {
    title: 'Error accessing NFC on your device',
    body: 'You may need to open this link in Chrome if that is not your current browser.',
    action: 'AdroindNFCGenericError',
    helper: CopyHelper,
  },
};

export const WebNFCChecker = ({
  mockEnableNfc,
  errorOverride,
  children,
}: {
  mockEnableNfc?: boolean;
  errorOverride?: string;
  children: React.ReactNode;
}) => {
  // Wrap the AndroidScreen and the MessageScreen in the Higher Order
  // withWebNfcChecks Component. The withWebNfcChecks handles checking
  // the Browser WebNFC and the Device NFC permissions, pops up dialogs,
  // and guides the user to accept the permissions. If WebNFC is supported,
  // and all permissions are accepted, the AndroidScreen is displayed.
  // Otherwise, the MessageScreen is displayed if the user presses the Cancel
  // button in a dialog without addressing the error, or if that error can't
  // be addressed.
  const AndroidScreenWithNfcHOC = withWebNfcChecks(
    () => children,
    ({ airKeyError }: { airKeyError: string }) => {
      // See https://github-pages.cloud.capitalone.com/AirKey/airkey-web-sdk/global.html#ERROR_DEVICE_NFC_NOT_ENABLED

      const errorMsg = errorOverride || airKeyError;

      if (
        errorMsg === 'ERROR_DEVICE_NFC_NOT_ENABLED' ||
        errorMsg === 'ERROR_NEED_NFC_PERMISSIONS'
      ) {
        return (
          <NFCNotEnabled source="sdk_web_nfc_checker" airKeyError={errorMsg} />
        );
      }

      const { title, body, action, helper } = errorMap[errorMsg]
        ? errorMap[errorMsg]
        : errorMap.GENERIC;

      return (
        <ErrorScreen_DEPRECIATED
          title={title}
          body={body}
          action={action}
          helper={helper}
        />
      );
    },
    mockEnableNfc,
  );

  if (mockEnableNfc) {
    return children;
  }

  return (
    <AndroidScreenWithNfcHOC
      webNfcPermissionsVideo={WEB_NFC_PERMISSIONS_VIDEO}
    />
  );
};
